import { useState } from 'react'
import SlideCard from './SlideCard'
import { SLIDES } from './slideData'
import type { Slide } from './slideData'
import { getOrCreateSessionId } from '@/utils/sessionId'
import { recordSlideResponse } from '@/services/validationApi'

interface ValidationCarouselProps {
  onYes: (slide: Slide) => void
  onNo: (slide: Slide) => void
}

export default function ValidationCarousel({ onYes, onNo }: ValidationCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const slide = SLIDES[currentIndex]
  const sessionId = getOrCreateSessionId()

  function handleResponse(type: 'yes' | 'no' | 'skip') {
    // Fire-and-forget — don't block UI
    recordSlideResponse({
      sessionId,
      slideIndex: slide.index,
      slideTitle: slide.title,
      responseType: type,
    }).catch(() => {})

    if (type === 'yes') {
      onYes(slide)
    } else if (type === 'no') {
      onNo(slide)
    } else if (currentIndex < SLIDES.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xl font-bold text-blue-600">TaxHive</span>
        <div className="flex items-center gap-1.5">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`h-2 w-2 rounded-full transition-colors ${
                i === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
        <SlideCard slide={slide} />

        {/* CTA */}
        <div className="mt-4">
          <p className="mb-4 text-center text-sm font-medium text-gray-700">
            Is this a real problem in your practice?
          </p>
          <div className="space-y-3">
            <button
              className="w-full rounded-lg bg-green-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              onClick={() => handleResponse('yes')}
            >
              Yes, this affects us
            </button>
            <button
              className="w-full rounded-lg border-2 border-gray-300 py-3 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              onClick={() => handleResponse('no')}
            >
              No, we manage this fine
            </button>
          </div>

          {currentIndex < SLIDES.length - 1 && (
            <button
              className="mt-4 block w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => handleResponse('skip')}
            >
              Skip &rarr;
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          {currentIndex + 1} of {SLIDES.length}
        </p>
      </div>
    </div>
  )
}
